import { OrderedMap } from "immutable";
import { BaseExercise } from "@domain/exercise/exercise";
import { exerciseFilterManager, exerciseManager } from "@core/service-container";
import { useObservable } from "micro-observables";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { FilterCategory } from "@domain/filters/filter";
import { ExerciseFilter } from "@domain/exercise/exercise-filter-manager";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import {
	AnalyticsViewExerciseDetailOrigin,
	sendAnalyticsEventViewExerciseDetail,
} from "@domain/library/exercise-analytics";
import { useExerciseFilters } from "@ui/components/exercise/hooks/use-exercise-filters";
import { ExerciseFetchState } from "@domain/exercise/exercise-manager";

const EQUIPMENT_FILTER_ID = "equipment";

interface UseExercisePageHook {
	filteredExercises: BaseExercise[];
	selectedExercises: BaseExercise[];
	activeFiltersMap: FiltersMap;
	search: string;
	isLoading: boolean;
	isError: boolean;
	refreshExercises: () => void;
	openFilteredExerciseDetailsModal: (exercise: BaseExercise) => void;
	openSelectedExerciseDetailsModal: (exercise: BaseExercise) => void;
	clearAllActiveFilters: () => void;
	updateActiveFilters: (category: FilterCategory<ExerciseFilter>, filter: ExerciseFilter, isActive: boolean) => void;
	activeFilters: FilterCategory<ExerciseFilter>[];
}

export const useExercisePage = (): UseExercisePageHook => {
	const dialogContext = useContext(DialogContext);
	const trackingContext = useContext(TrackingContext);
	const search = useObservable(exerciseFilterManager.searchWord);
	const filteredExercises = useObservable(exerciseFilterManager.filteredExercises);
	const selectedExercises = useObservable(exerciseFilterManager.selectedExercises);
	const activeFilters = useObservable(exerciseFilterManager.activeFilters);
	const filters = useExerciseFilters();

	useEffect(() => {
		if (exerciseManager.exercisesStatus.get() === ExerciseFetchState.Undefined) {
			exerciseManager.fetchExercises();
		}
	}, []);

	const activeFiltersMap: FiltersMap = useMemo(
		() =>
			OrderedMap(
				activeFilters.map(category => [
					category.id,
					{ ...category, filters: OrderedMap(category.filters.map(filter => [filter.id, filter])) },
				]),
			),
		[activeFilters],
	);

	const [refreshExercisesAsync, isLoading, isError] = useAsyncOperation(
		useCallback(async () => {
			exerciseFilterManager.clearAllSelectedExercises();
			exerciseFilterManager.clearAllFiltersAndSearch();
			await exerciseFilterManager.fetchExercises();
			exerciseFilterManager.filterExercises();
		}, []),
		{
			initialLoadingState: true,
		},
	);

	const openExerciseDetailsModal = useCallback(
		(exercise: BaseExercise, relatedExercises: BaseExercise[]) => {
			dialogContext?.openModal(ModalId.ExerciseDetails, {
				relatedExercises: relatedExercises,
				exerciseIndex: relatedExercises.findIndex(ex => ex === exercise),
				tabHeaderDisabled: true,
				trackingContext,
				editorName: "",
			});
			sendAnalyticsEventViewExerciseDetail(exercise, AnalyticsViewExerciseDetailOrigin.EXERCISE);
		},
		[dialogContext, trackingContext],
	);

	const openFilteredExerciseDetailsModal = useCallback(
		(exercise: BaseExercise) => {
			openExerciseDetailsModal(exercise, filteredExercises);
		},
		[filteredExercises, openExerciseDetailsModal],
	);

	const openSelectedExerciseDetailsModal = useCallback(
		(exercise: BaseExercise) => {
			openExerciseDetailsModal(exercise, selectedExercises);
		},
		[openExerciseDetailsModal, selectedExercises],
	);

	const clearAllActiveFilters = useCallback(() => {
		exerciseFilterManager.activeFilters.set([]);
	}, []);

	const selectedEquipment = useMemo(() => {
		const equipmentCategory = activeFilters.find(cat => cat.id === EQUIPMENT_FILTER_ID);
		return equipmentCategory ? equipmentCategory.filters.map(f => f.id) : [];
	}, [activeFilters]);

	const updateActiveFilters = useCallback(
		(category: FilterCategory<ExerciseFilter>, filter: ExerciseFilter, isActive: boolean): void => {
			const categoryFilters = activeFiltersMap.get(category.id)?.filters ?? OrderedMap();
			const newMap = activeFiltersMap.set(category.id, {
				...category,
				filters: isActive ? categoryFilters.set(filter.id, filter) : categoryFilters.remove(filter.id),
			});
			exerciseFilterManager.activeFilters.set(internalFilterMapToArray(newMap, filters, isActive));
		},
		[activeFiltersMap, filters]
	);

	const filteredExercisesWithEquipment = useMemo(() => {
		if (selectedEquipment.length === 0 && activeFilters.find(cat => cat.id === EQUIPMENT_FILTER_ID)) {
			return [];
		}
		return filteredExercises;
	}, [filteredExercises, selectedEquipment, activeFilters]);

	return {
		// allExercises,
		// exerciseTotalCount,
		filteredExercises: filteredExercisesWithEquipment,
		selectedExercises,
		activeFiltersMap,
		search,
		isLoading,
		isError,
		refreshExercises: () => { void refreshExercisesAsync(); },
		openFilteredExerciseDetailsModal,
		openSelectedExerciseDetailsModal,
		clearAllActiveFilters,
		updateActiveFilters,
		activeFilters,
	};
}

interface InternalFilterCategory extends Omit<FilterCategory<ExerciseFilter>, "filters"> {
	filters: OrderedMap<string, ExerciseFilter>;
}

type FiltersMap = OrderedMap<string, InternalFilterCategory>;

const internalFilterMapToArray = (
	map: FiltersMap,
	allFilters: FilterCategory<ExerciseFilter>[],
	shouldSort: boolean,
): FilterCategory<ExerciseFilter>[] =>
	shouldSort
		? allFilters.reduce((categories, category) => {
				const mapCategory = map.get(category.id);
				return mapCategory?.filters.size
					? [
							...categories,
							{
								...mapCategory,
								filters: category.filters.reduce((filters, filter) => {
									const mapFilter = mapCategory.filters.get(filter.id);
									return mapFilter ? [...filters, mapFilter] : filters;
								}, [] as ExerciseFilter[]),
							},
						]
					: categories;
			}, [] as FilterCategory<ExerciseFilter>[])
		: Array.from(map.values()).reduce((categories, category) => {
				const filtersArray = Array.from(category.filters.values());
				return filtersArray.length === 0 ? categories : [...categories, { ...category, filters: filtersArray }];
			}, [] as FilterCategory<ExerciseFilter>[]);
